import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import postsService from '../services/posts';
import './PostsApproval.css';

const PostsApproval = () => {
  const [passwordPop, setPasswordPop] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState();
  const [filter, setFilter] = useState('all');
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const navigate = useNavigate();

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetchPosts();
      setPasswordPop(false);
    } catch (error) {
      setPosts([]);
      window.alert('Failed to fetch encrypted fields', error.message);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const rawPosts = await postsService.getPosts(password);
      if (rawPosts.error) {
        window.alert(rawPosts.error);
        return;
      }

      const reversedPosts = rawPosts.posts.reverse();
      setPosts(reversedPosts);
      setFilteredPosts(reversedPosts);
      setFilter('all');
    } catch (error) {
      setPosts([]);
      window.alert('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const selectedFilter = e.target.value || 'all';
    setFilter(selectedFilter);
    setFilteredPosts(
      selectedFilter === 'all'
        ? posts
        : posts.filter((post) => post.adminApproval === selectedFilter)
    );
  };

  const handlePostUpdate = async (postUpdateData) => {
    try {
      const updatedPost = await postsService.updatePost({
        password,
        post: postUpdateData,
      });

      if (updatedPost.error) {
        window.alert(updatedPost.error);
        return;
      }

      setPosts((prev) =>
        prev.map((p) => (p._id === updatedPost._id ? updatedPost : p))
      );
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      window.alert(`Failed to update post: ${error.message}`);
    }
  };

  const updateDetails = () => {
    handlePostUpdate(selectedPost);
  };

  const applyChangesAndApprove = () => {
    handlePostUpdate({ ...selectedPost, adminApproval: 'approved' });
  };

  const rejectPost = () => {
    handlePostUpdate({
      ...selectedPost,
      adminApproval: 'rejected',
      rejectNote,
    });
  };

  const handleClosePasswordPopup = () => {
    setPassword('');
    setError('');
    navigate(-1);
  };

  return (
    <>
      <div>
        {passwordPop ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <form
              onSubmit={handlePasswordSubmit}
              className="relative bg-white w-md max-w-sm rounded-xl shadow-xl p-6"
            >
              <span
                onClick={handleClosePasswordPopup}
                role="button"
                aria-label="Close"
                className="
              absolute
              top-3
              right-3
              text-gray-500
              text-xl
              cursor-pointer
              hover:text-gray-800
              select-none
            "
              >
                ✕
              </span>

              <h2 className="text-xl font-semibold text-gray-800 text-center mb-4 mt-4">
                Enter Master Password
              </h2>

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-lg
                      text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
              />

              <button
                type="submit"
                className="w-full py-2 rounded-lg text-sm font-medium
                      bg-green-600 text-white hover:bg-green-700 transition"
              >
                Submit
              </button>
            </form>
          </div>
        ) : (
          <div className="tableholder">
            {!selectedPost && (
              <div
                className="
              flex
              flex-col
              sm:flex-row
              sm:items-center
              sm:justify-between
              gap-3
              mb-4
            "
              >
                <button
                  onClick={() => navigate(-1)}
                  className="
                  inline-flex
                  items-center
                  gap-2
                  px-3
                  py-2
                  rounded-md
                  bg-gray-200
                  text-gray-700
                  text-sm
                  font-medium
                  hover:bg-gray-300
                  transition
                  w-fit
                "
                >
                  <ArrowLeft size={16} />
                  Back
                </button>

                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    handleFilterChange(e);
                  }}
                  className="
                  w-full
                  sm:w-auto
                  px-3
                  py-2
                  rounded-md
                  border
                  border-gray-300
                  text-sm
                  bg-white
                  focus:outline-none
                  focus:ring-2
                  focus:ring-blue-500
                "
                >
                  <option value="all">All</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            )}

            {!selectedPost && (
              <div className="w-full overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>City</th>
                      <th>
                        State or
                        <br />
                        Province
                      </th>
                      <th>
                        Current Premium
                        <br />
                        (per month)
                      </th>
                      <th>Posted on</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan="100%" className="text-center py-10">
                          <img
                            src="/loading.gif"
                            alt="Loading..."
                            className="mx-auto max-w-[50%]"
                          />
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      filteredPosts.map((post) => (
                        <tr key={post._id} onClick={() => setSelectedPost(post)}>
                          <td>
                            {typeof post.type === 'object'
                              ? post.type.name
                              : post.type}
                          </td>
                          <td className="capitalize">{post.city}</td>
                          <td className="capitalize">{post.state}</td>
                          <td>{post.currentPremium || '-'}</td>
                          <td>{post.uploadDate}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedPost && (
              <div style={{ margin: '20px' }}>
                <h1 style={{ margin: '40px 20px' }}>Post Details:</h1>
                {Object.entries(selectedPost).map(([key, value]) => (
                  <div key={key} style={{ marginBottom: '15px' }}>
                    {![
                      '_id',
                      'bids',
                      'additionalData',
                      'adminApproval',
                      'rejectNote',
                      'userId',
                      'userEmail',
                      'visibility',
                      'listingId',
                      'type',
                    ].includes(key) && (
                        <>
                          <strong className="capitalize">{key}:</strong>
                          <input
                            value={value || '-'}
                            onChange={(e) =>
                              setSelectedPost({
                                ...selectedPost,
                                [key]: e.target.value,
                              })
                            }
                            style={{ width: 'auto' }}
                          />
                        </>
                      )}

                    {[
                      '_id',
                      'listingId',
                      'type',
                      'userEmail',
                      'adminApproval',
                      'visibility',
                    ].includes(key) && (
                        <>
                          <strong className="capitalize">{key}:</strong>
                          <p>
                            {typeof value === 'boolean'
                              ? value.toString()
                              : value}
                          </p>
                        </>
                      )}

                    {key === 'additionalData' &&
                      Object.entries(value).map(([subKey, subValue]) => (
                        <div key={subKey} style={{ marginBottom: '10px' }}>
                          <strong className="capitalize">{subKey}:</strong>
                          <input
                            value={subValue || '-'}
                            onChange={(e) =>
                              setSelectedPost({
                                ...selectedPost,
                                additionalData: {
                                  ...selectedPost.additionalData,
                                  [subKey]: e.target.value,
                                },
                              })
                            }
                            style={{ width: 'auto' }}
                          />
                        </div>
                      ))}
                  </div>
                ))}

                <div className="flex flex-wrap gap-4 mt-4">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setSelectedPost(null);
                    }}
                  >
                    Back to Posts
                  </button>
                  <button className="btn btn-success" onClick={updateDetails}>
                    Save Changes
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={applyChangesAndApprove}
                  >
                    Save changes and Approve post
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => setRejecting(true)}
                  >
                    Reject Post
                  </button>
                </div>

                {rejecting && (
                  <div className="mt-4">
                    <textarea
                      placeholder="Enter rejection note"
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      style={{ width: '100%', height: '100px' }}
                    />
                    <div className="flex gap-4 mt-2">
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          rejectPost();
                          setRejecting(false);
                          setRejectNote('');
                        }}
                      >
                        Reject Post
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setRejecting(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
export default PostsApproval;
